import type { Camera } from '../../services/camerasApi';
import type { Line } from '../../services/linesApi';
import type { Policy } from '../../services/policiesApi';
import type { Zone } from '../../services/zonesApi';
import { isRestrictedZone } from './policyPacks';

export type PolicyScopeLookups = {
  camerasById?: ReadonlyMap<string, Camera>;
  zonesById?: ReadonlyMap<string, Zone>;
  linesById?: ReadonlyMap<string, Line>;
};

export type PolicyScopeDetails = {
  scopeKind: 'whole_camera' | 'zones' | 'line' | 'unset';
  summary: string;
  chips: string[];
  zoneNames: string[];
  lineName?: string;
  unresolvedZoneIds: string[];
};

function getZoneLabel(zone: Zone): string {
  return String(zone.name || zone.zone_id || 'Unnamed zone').trim();
}

function getLineLabel(line: Line): string {
  return String(line.name || line.line_id || 'Unnamed line').trim();
}

function resolveZoneNames(policy: Policy, zonesById?: ReadonlyMap<string, Zone>): {
  zones: Zone[];
  zoneNames: string[];
  unresolvedZoneIds: string[];
} {
  const zoneIds = Array.isArray(policy.scope?.zone_ids) ? policy.scope.zone_ids.map((value) => String(value)) : [];
  const zones = zoneIds
    .map((zoneId) => zonesById?.get(zoneId))
    .filter((zone): zone is Zone => Boolean(zone));
  const zoneNames = zones.map(getZoneLabel);
  const resolved = new Set(zones.map((zone) => String(zone.zone_id)));
  const unresolvedZoneIds = zoneIds.filter((zoneId) => !resolved.has(zoneId));
  return { zones, zoneNames, unresolvedZoneIds };
}

function resolveLine(policy: Policy, linesById?: ReadonlyMap<string, Line>): Line | null {
  const lineId = String((policy.config as any)?.line_id || (policy.config as any)?.line?.line_id || '').trim();
  if (!lineId) return null;
  return linesById?.get(lineId) || null;
}

function buildZoneSummary(zones: Zone[], zoneNames: string[], unresolvedZoneIds: string[], maxVisible: number): string {
  if (zones.length === 1 && unresolvedZoneIds.length === 0 && isRestrictedZone(zones[0])) {
    return `Restricted zone — ${zoneNames[0]}`;
  }

  const visibleNames = zoneNames.slice(0, maxVisible);
  const hiddenCount = Math.max(zoneNames.length - visibleNames.length, 0) + unresolvedZoneIds.length;
  let summary = visibleNames.length > 0 ? `Zones — ${visibleNames.join(', ')}` : 'Zones';
  if (hiddenCount > 0) summary += `, +${hiddenCount} more`;
  return summary;
}

export function buildPolicyDetailsScope(policy: Policy, lookups: PolicyScopeLookups = {}): PolicyScopeDetails {
  const { zonesById, linesById } = lookups;
  const line = resolveLine(policy, linesById);
  if (policy.policy_type === 'line_crossing') {
    const lineName = line ? getLineLabel(line) : '';
    return {
      scopeKind: 'line',
      summary: lineName ? `Line — ${lineName}` : 'Line — Missing line',
      chips: lineName ? [lineName] : [],
      lineName: lineName || undefined,
      zoneNames: [],
      unresolvedZoneIds: [],
    };
  }

  const { zones, zoneNames, unresolvedZoneIds } = resolveZoneNames(policy, zonesById);
  if (zoneNames.length > 0 || unresolvedZoneIds.length > 0) {
    const visibleNames = zoneNames.slice(0, 8);
    const hiddenCount = Math.max(zoneNames.length - visibleNames.length, 0) + unresolvedZoneIds.length;
    const chips = hiddenCount > 0 ? [...visibleNames, `+${hiddenCount} more`] : visibleNames;
    return {
      scopeKind: 'zones',
      summary: buildZoneSummary(zones, zoneNames, unresolvedZoneIds, 8),
      chips,
      zoneNames,
      unresolvedZoneIds,
    };
  }

  const geometryMode = String(policy.scope?.geometry_mode || '').trim().toLowerCase();
  const hasCamera = Boolean(policy.camera_id || policy.target?.camera_id || policy.scope?.camera_ids?.length);
  if (geometryMode === 'whole_view' || hasCamera) {
    return {
      scopeKind: 'whole_camera',
      summary: 'Whole camera',
      chips: ['Whole camera'],
      zoneNames: [],
      unresolvedZoneIds: [],
    };
  }

  return {
    scopeKind: 'unset',
    summary: 'Scope not set',
    chips: [],
    zoneNames: [],
    unresolvedZoneIds: [],
  };
}

export function buildPolicyScopeSummary(
  policy: Policy,
  lookups: PolicyScopeLookups = {},
  maxVisible = 2,
): string {
  const details = buildPolicyDetailsScope(policy, lookups);
  if (details.scopeKind !== 'zones') return details.summary;

  const { zonesById } = lookups;
  const { zones, zoneNames, unresolvedZoneIds } = resolveZoneNames(policy, zonesById);
  return buildZoneSummary(zones, zoneNames, unresolvedZoneIds, maxVisible);
}

export function buildPolicyScopeChips(
  policy: Policy,
  lookups: PolicyScopeLookups = {},
  maxVisible = 2,
): string[] {
  const details = buildPolicyDetailsScope(policy, lookups);
  if (details.scopeKind === 'line' || details.scopeKind === 'whole_camera' || details.scopeKind === 'unset') {
    return details.chips;
  }

  const visibleNames = details.zoneNames.slice(0, maxVisible);
  const hiddenCount = Math.max(details.zoneNames.length - visibleNames.length, 0) + details.unresolvedZoneIds.length;
  return hiddenCount > 0 ? [...visibleNames, `+${hiddenCount} more`] : visibleNames;
}
